import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DocumentManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [documentType, setDocumentType] = useState('all');
  const [selectedDocs, setSelectedDocs] = useState(new Set());

  const documentTypeOptions = [
    { value: 'all', label: 'All Documents' },
    { value: 'msds', label: 'Safety Data Sheets' },
    { value: 'application', label: 'Application Records' },
    { value: 'certification', label: 'Certifications' },
    { value: 'inspection', label: 'Inspection Reports' },
    { value: 'training', label: 'Training Records' }
  ];

  const documents = [
    {
      id: 1,
      name: "Glyphosate 41% - Safety Data Sheet",
      type: "msds",
      category: "Safety Data Sheets",
      uploadDate: "2024-10-15",
      expiryDate: "2025-10-15",
      size: "2.4 MB",
      format: "PDF",
      status: "current",
      downloadCount: 23,
      lastAccessed: "2024-11-14",
      complianceRequired: true,
      auditTrail: true,
      icon: "FileText"
    },
    {
      id: 2,
      name: "Field A-12 Application Record - Nov 2024",
      type: "application",
      category: "Application Records",
      uploadDate: "2024-11-15",
      expiryDate: null,
      size: "1.8 MB",
      format: "PDF",
      status: "current",
      downloadCount: 8,
      lastAccessed: "2024-11-15",
      complianceRequired: true,
      auditTrail: true,
      icon: "Clipboard"
    },
    {
      id: 3,
      name: "Organic Certification - 2024",
      type: "certification",
      category: "Certifications",
      uploadDate: "2024-01-15",
      expiryDate: "2024-12-31",
      size: "3.2 MB",
      format: "PDF",
      status: "expiring",
      downloadCount: 45,
      lastAccessed: "2024-11-10",
      complianceRequired: true,
      auditTrail: true,
      icon: "Award"
    },
    {
      id: 4,
      name: "EPA Compliance Inspection Report",
      type: "inspection",
      category: "Inspection Reports",
      uploadDate: "2024-09-20",
      expiryDate: null,
      size: "4.1 MB",
      format: "PDF",
      status: "current",
      downloadCount: 12,
      lastAccessed: "2024-11-08",
      complianceRequired: true,
      auditTrail: true,
      icon: "Search"
    },
    {
      id: 5,
      name: "Pesticide Applicator License - M. Rodriguez",
      type: "certification",
      category: "Certifications",
      uploadDate: "2024-03-15",
      expiryDate: "2025-03-15",
      size: "0.8 MB",
      format: "PDF",
      status: "current",
      downloadCount: 6,
      lastAccessed: "2024-11-12",
      complianceRequired: true,
      auditTrail: true,
      icon: "UserCheck"
    },
    {
      id: 6,
      name: "Chemical Handling Training Certificate",
      type: "training",
      category: "Training Records",
      uploadDate: "2024-08-10",
      expiryDate: "2025-08-10",
      size: "1.2 MB",
      format: "PDF",
      status: "current",
      downloadCount: 15,
      lastAccessed: "2024-11-05",
      complianceRequired: true,
      auditTrail: true,
      icon: "GraduationCap"
    },
    {
      id: 7,
      name: "Chlorpyrifos MSDS - Updated",
      type: "msds",
      category: "Safety Data Sheets",
      uploadDate: "2024-11-01",
      expiryDate: "2025-11-01",
      size: "2.1 MB",
      format: "PDF",
      status: "current",
      downloadCount: 18,
      lastAccessed: "2024-11-13",
      complianceRequired: true,
      auditTrail: true,
      icon: "FileText"
    },
    {
      id: 8,
      name: "Weather Monitoring Log - October 2024",
      type: "application",
      category: "Application Records",
      uploadDate: "2024-11-01",
      expiryDate: null,
      size: "0.9 MB",
      format: "Excel",
      status: "current",
      downloadCount: 7,
      lastAccessed: "2024-11-09",
      complianceRequired: false,
      auditTrail: true,
      icon: "Cloud"
    }
  ];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'current':
        return { color: 'text-success', bg: 'bg-success/10', label: 'Current' };
      case 'expiring':
        return { color: 'text-warning', bg: 'bg-warning/10', label: 'Expiring Soon' };
      case 'expired':
        return { color: 'text-error', bg: 'bg-error/10', label: 'Expired' };
      default:
        return { color: 'text-muted-foreground', bg: 'bg-muted/10', label: 'Unknown' };
    }
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const filteredDocuments = documents?.filter(doc => {
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         doc?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesType = documentType === 'all' || doc?.type === documentType;
    return matchesSearch && matchesType;
  });

  const handleSelectDocument = (docId) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected?.has(docId)) {
      newSelected?.delete(docId);
    } else {
      newSelected?.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDocs?.size === filteredDocuments?.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(filteredDocuments.map(doc => doc.id)));
    }
  };

  const documentStats = {
    total: documents?.length,
    current: documents?.filter(doc => doc?.status === 'current')?.length,
    expiring: documents?.filter(doc => isExpiringSoon(doc?.expiryDate))?.length,
    compliance: documents?.filter(doc => doc?.complianceRequired)?.length
  };

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Document Management</h3>
            <p className="text-sm text-muted-foreground">
              Manage compliance documents and audit preparation materials
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <Input
              type="search"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            
            <Select
              options={documentTypeOptions}
              value={documentType}
              onChange={setDocumentType}
              className="w-full sm:w-48"
            />
            
            <Button variant="default" iconName="Upload" iconPosition="left">
              Upload
            </Button>
          </div>
        </div>

        {/* Document Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="FileText" size={16} className="text-primary" />
              <span className="text-sm text-muted-foreground">Total</span>
            </div>
            <div className="text-xl font-semibold text-foreground">{documentStats?.total}</div>
          </div>
          
          <div className="p-3 bg-success/10 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm text-muted-foreground">Current</span>
            </div>
            <div className="text-xl font-semibold text-success">{documentStats?.current}</div>
          </div>
          
          <div className="p-3 bg-warning/10 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-warning" />
              <span className="text-sm text-muted-foreground">Expiring</span>
            </div>
            <div className="text-xl font-semibold text-warning">{documentStats?.expiring}</div>
          </div>
          
          <div className="p-3 bg-primary/10 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-primary" />
              <span className="text-sm text-muted-foreground">Compliance</span>
            </div>
            <div className="text-xl font-semibold text-primary">{documentStats?.compliance}</div>
          </div>
        </div>
      </div>
      {/* Bulk Actions */}
      {selectedDocs?.size > 0 && (
        <div className="p-4 bg-primary/5 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {selectedDocs?.size} document{selectedDocs?.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Download Selected
              </Button>
              <Button variant="outline" size="sm" iconName="Archive">
                Archive
              </Button>
              <Button variant="outline" size="sm" iconName="Trash2" className="text-error">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Document List */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left p-4">
                <input
                  type="checkbox"
                  checked={selectedDocs?.size === filteredDocuments?.length && filteredDocuments?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">Document</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Size</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Last Accessed</th>
              <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocuments?.map((doc) => {
              const statusConfig = getStatusConfig(doc?.status);
              const expiringSoon = isExpiringSoon(doc?.expiryDate);
              
              return (
                <tr key={doc?.id} className="border-b border-border hover:bg-muted/20 smooth-transition">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedDocs?.has(doc?.id)}
                      onChange={() => handleSelectDocument(doc?.id)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-muted/30 rounded-lg">
                        <Icon name={doc?.icon} size={16} className="text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{doc?.name}</div>
                        <div className="text-sm text-muted-foreground flex items-center space-x-2">
                          <span>Uploaded: {new Date(doc.uploadDate)?.toLocaleDateString()}</span>
                          {doc?.complianceRequired && (
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                              Compliance Required
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-muted-foreground">{doc?.category}</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig?.bg} ${statusConfig?.color}`}>
                        {statusConfig?.label}
                      </span>
                      {doc?.expiryDate && (
                        <div className="text-xs text-muted-foreground">
                          Expires: {new Date(doc.expiryDate)?.toLocaleDateString()}
                          {expiringSoon && (
                            <Icon name="AlertTriangle" size={12} className="inline ml-1 text-warning" />
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-muted-foreground">
                      <div>{doc?.size}</div>
                      <div className="text-xs">{doc?.format}</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-muted-foreground">
                      <div>{new Date(doc.lastAccessed)?.toLocaleDateString()}</div>
                      <div className="text-xs">{doc?.downloadCount} downloads</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm" title="Download">
                        <Icon name="Download" size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" title="View">
                        <Icon name="Eye" size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" title="Share">
                        <Icon name="Share2" size={16} />
                      </Button>
                      {doc?.auditTrail && (
                        <Button variant="ghost" size="sm" title="Audit Trail">
                          <Icon name="History" size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {filteredDocuments?.length} of {documents?.length} documents</span>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagement;